import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Button as MuiButton,
  Select,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import { TOAST_MESSAGES } from "~/constants/messages";
import { OrganizationApi } from "~/functions/api/organization";
import { UserApi } from "~/functions/api/user";
import AddWhiteIcon from "~/images/add_white_icon.svg?react";
import ContextMenu from "~/images/context_menu.svg?react";
import type { User } from "~/interface/user";
import { removeMemberAction, updateMemberRoleAction } from "~/slices/organizationSlice";
import type { RootState } from "~/store";
import { getRoleDisplayText, getRolePriority } from "~/utils/roleUtils";
import InviteMemberModal from "../inviteMemberModal";
import styles from "./index.module.scss";

// User型にroleForLogicを追加した型を定義
type ProcessedUser = User & { roleForLogic: string };

// Helper function to check if a user can be deleted
// Combines all deletion permission checks in one place:
// 1. Only admin can delete users (not manager, member, or guest)
// 2. Cannot delete self
// 3. Cannot delete root user
type RootUser = { id: string; firstName: string; lastName: string; displayName: string };
const canDeleteUser = (
  member: ProcessedUser,
  currentUser: { id?: string; role?: string } | undefined,
  rootUser: RootUser | undefined,
): boolean => {
  if (!currentUser) return false;
  // Permission: only admin can delete
  if (currentUser.role !== "admin") return false;
  // Self-deletion prevention
  if (member.id === currentUser.id) return false;
  // Root user protection
  if (rootUser && member.id === rootUser.id) return false;
  return true;
};

const getRoleStyle = (role: string): string => {
  switch (role) {
    case "admin":
      return styles["adminRole"] || "";
    case "manager":
      return styles["projectAdminRole"] || "";
    default:
      return styles["userRole"] || "";
  }
};

// 選択可能なロール
const availableRoles = [
  { value: "admin", label: "管理者" },
  { value: "manager", label: "プロジェクト管理者" },
  { value: "member", label: "ユーザー" },
];

export const MemberList: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const organizationData = useSelector((state: RootState) => state.organization.organization);
  const membersRaw = useSelector((state: RootState) => state.organization.members);
  const members = useMemo(() => membersRaw || [], [membersRaw]);
  const dispatch = useDispatch();

  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUserForRoleChange, setSelectedUserForRoleChange] = useState<ProcessedUser | null>(
    null,
  );
  const [newRole, setNewRole] = useState<string>("");

  // Get max limits from organization plan
  const maxMembers = organization?.plan?.limits?.maxMembers || 0;

  const processedMembers = useMemo(() => {
    return (members || []).map((member) => ({
      ...member,
      roleForLogic: member.role,
    }));
  }, [members]);

  const sortedMembers = useMemo(() => {
    return [...processedMembers].sort((a, b) => {
      return getRolePriority(a.roleForLogic) - getRolePriority(b.roleForLogic);
    });
  }, [processedMembers]);

  // Only admin can see and use the context menu (for role change and delete)
  const isAdmin = currentUser?.role === "admin";

  const handleRoleChange = async (userId: string, role: string) => {
    const userApi = new UserApi();
    const response = await userApi.updateRole(currentUser?.organization_id, userId, { role: role });
    if (response === true) {
      toast.success(TOAST_MESSAGES.ORGANIZATION.CHANGE_ROLE_SUCCESS);
      return true;
    } else {
      toast.error(TOAST_MESSAGES.ORGANIZATION.CHANGE_ROLE_FAILED);
      return false;
    }
  };

  const handleRemoveUser = async (userId: string) => {
    const userApi = new UserApi();
    const response = await userApi.delete(currentUser?.organization_id, userId);
    if (response.success) {
      dispatch(removeMemberAction(userId));
      toast.success(TOAST_MESSAGES.ORGANIZATION.DELETE_USER_SUCCESS);
    } else {
      // エラーメッセージがAPIから伝播される
      toast.error(response.error);
    }
    setDropdownOpen(null);
  };

  const handleOpenRoleModal = (member: ProcessedUser) => {
    setSelectedUserForRoleChange(member);
    setNewRole(member.roleForLogic);
    setIsRoleModalOpen(true);
    setDropdownOpen(null);
  };

  const handleCloseRoleModal = () => {
    setIsRoleModalOpen(false);
    setSelectedUserForRoleChange(null);
    setNewRole("");
  };

  const handleConfirmRoleChange = async () => {
    if (
      selectedUserForRoleChange &&
      newRole &&
      newRole !== selectedUserForRoleChange.roleForLogic
    ) {
      const success = await handleRoleChange(selectedUserForRoleChange.id, newRole);
      if (success) {
        // Reduxストアの該当メンバーの権限を更新
        dispatch(
          updateMemberRoleAction({
            userId: selectedUserForRoleChange.id,
            role: newRole,
          }),
        );
        handleCloseRoleModal();
      }
    }
  };

  const handleResendInvite = async (userId: string) => {
    const organizationApi = new OrganizationApi();
    const response = await organizationApi.sendInviteEmail(currentUser?.organization_id, userId);
    if (response.success) {
      toast.success(TOAST_MESSAGES.ORGANIZATION.RESEND_INVITE_SUCCESS);
    } else {
      // エラーメッセージがAPIから伝播される
      toast.error(response.error);
    }
  };

  return (
    <div className={styles["listContent"]}>
      <div className="flex justify-between items-center w-full font-sans">
        <h1>メンバー</h1>
        <div className="flex-end flex gap-[4px] flex-col">
          {currentUser.role !== "guest" && (
            <CommonButton
              onClick={() => setModalOpen(true)}
              className="w-[144px]"
              labelClassName="!px-0"
              icon={<AddWhiteIcon />}
              disabled={sortedMembers.length >= maxMembers}
              data-testid="add-member-button"
            >
              メンバー追加
            </CommonButton>
          )}
          <span className="text-[11px] text-gray">
            利用可能メンバー: {`${sortedMembers.length}/${maxMembers}`}
          </span>
        </div>
      </div>

      <ul className={styles["memberList"]}>
        {sortedMembers?.map((member, index) => (
          <li className={styles["memberItem"]} key={member.id}>
            <div className={styles["memberAvatar"]}>
              <AccountCircleIcon style={{ fontSize: 40, color: "#1D9FD6" }} />
            </div>
            <div className={styles["memberDetails"]}>
              <span className={styles["memberInfo"]}>{member.display_name}</span>
              {member.status === "invited" ? (
                <div className={styles["invitedMemberInfo"]}>
                  <span className={styles["memberRole"]}>招待中</span>
                  <Button
                    label="招待メール再送信"
                    styleType="white-tiny"
                    onClick={() => handleResendInvite(member.id)}
                  />
                </div>
              ) : (
                <span className={`${styles["memberRole"]} ${getRoleStyle(member.roleForLogic)}`}>
                  <span>{getRoleDisplayText(member.roleForLogic)}</span>
                </span>
              )}
            </div>
            {isAdmin && (
              <div
                className={styles["moreOptions"]}
                ref={index === dropdownOpen ? dropdownRef : null}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(dropdownOpen === index ? null : index);
                  }}
                >
                  <ContextMenu />
                </button>

                {dropdownOpen === index && (
                  <div className={styles["dropdownMenu"]}>
                    <ul>
                      {member.roleForLogic !== "guest" && (
                        <li
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRoleModal(member);
                          }}
                        >
                          権限変更
                        </li>
                      )}
                      {canDeleteUser(member, currentUser, organizationData?.rootUser) && (
                        <li
                          className={styles["danger"]}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveUser(member.id);
                          }}
                        >
                          削除
                        </li>
                      )}
                      <li
                        className={styles["cancelOption"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen(null);
                        }}
                      >
                        キャンセル
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {selectedUserForRoleChange && (
        <Modal
          open={isRoleModalOpen}
          onClose={handleCloseRoleModal}
          aria-labelledby="role-change-modal-title"
          aria-describedby="role-change-modal-description"
        >
          <Box className={styles["roleChangeModal"]}>
            <Typography
              id="role-change-modal-title"
              variant="h6"
              component="h2"
              {...(styles["modalHeader"] && { className: styles["modalHeader"] })}
            >
              権限変更: {selectedUserForRoleChange.display_name}
            </Typography>
            <Typography
              sx={{ mt: 1, mb: 2, fontSize: "0.9rem", color: "text.secondary" }}
              {...(styles["modalSubHeader"] && { className: styles["modalSubHeader"] })}
            >
              現在の権限: {getRoleDisplayText(selectedUserForRoleChange.roleForLogic)}
            </Typography>
            <FormControl
              fullWidth
              sx={{ mt: 2, mb: 2 }}
              {...(styles["modalBody"] && { className: styles["modalBody"] })}
            >
              <InputLabel id="new-role-select-label">新しい権限</InputLabel>
              <Select
                labelId="new-role-select-label"
                id="new-role-select"
                value={newRole}
                label="新しい権限"
                onChange={(e) => setNewRole(e.target.value as string)}
              >
                {availableRoles.map((role) => (
                  <MenuItem
                    key={role.value}
                    value={role.value}
                    disabled={role.value === selectedUserForRoleChange.roleForLogic}
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
              {...(styles["modalActions"] && { className: styles["modalActions"] })}
            >
              <MuiButton
                onClick={handleCloseRoleModal}
                sx={{ mr: 1 }}
                {...(styles["cancelButton"] && { className: styles["cancelButton"] })}
              >
                キャンセル
              </MuiButton>
              <MuiButton
                onClick={handleConfirmRoleChange}
                variant="contained"
                disabled={!newRole || newRole === selectedUserForRoleChange.roleForLogic}
                {...(styles["confirmButton"] && { className: styles["confirmButton"] })}
              >
                変更
              </MuiButton>
            </Box>
          </Box>
        </Modal>
      )}

      {/* メンバー招待モーダル */}
      <InviteMemberModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};
